import { BreadBudget, BudgetLineItem, FirestoreDocCategory } from "breadcommon";
import dayjs, { Dayjs } from "dayjs";

/**
 * Returns the budget line item amount with appropriate sign based on category type.
 * - Income and Savings categories return positive amounts
 * - Expense categories return negative amounts
 * - Silent categories return the amount as-is
 */
export function getSignedBudgetLineItemAmount(
  lineItem: BudgetLineItem | undefined,
  category: FirestoreDocCategory,
): number {
  const amount = lineItem?.amount || 0;

  if (category.type === "Expense") {
    return -amount;
  }
  return amount;
}

/** Whether `today` falls within the budget's period, inclusive of both ends. */
export function isBudgetActive(budget: BreadBudget, today: Dayjs): boolean {
  return (
    !today.isBefore(budget.beginDateTimestampSecs, "day") &&
    !today.isAfter(budget.endDateTimestampSecs, "day")
  );
}

/** Budgets ordered by end date, latest first. */
export function sortBudgetsByEndDesc(
  budgets: Map<string, BreadBudget>,
): BreadBudget[] {
  return Array.from(budgets.values()).sort(
    (a, b) =>
      b.endDateTimestampSecs.valueOf() - a.endDateTimestampSecs.valueOf(),
  );
}

/**
 * The budget to show when the Budgets tab opens: one that's active today, or
 * failing that the one that ends last. An upcoming budget that's already been
 * planned shouldn't hide the one currently being tracked.
 */
export function pickDefaultBudgetId(
  budgets: Map<string, BreadBudget>,
  today: Dayjs = dayjs(),
): string | null {
  const sorted = sortBudgetsByEndDesc(budgets);
  return (
    sorted.find((budget) => isBudgetActive(budget, today))?.id ??
    sorted.at(0)?.id ??
    null
  );
}

/** The months a budget covers, e.g. "Jan – Dec 2026" or "Nov 2025 – Oct 2026". */
export function budgetMonths(budget: BreadBudget): string {
  const start = budget.beginDateTimestampSecs;
  const end = budget.endDateTimestampSecs;
  if (start.isSame(end, "month")) return start.format("MMM YYYY");
  return start.isSame(end, "year")
    ? `${start.format("MMM")} – ${end.format("MMM YYYY")}`
    : `${start.format("MMM YYYY")} – ${end.format("MMM YYYY")}`;
}

/**
 * How much of a budget's time has passed, e.g. "79% complete": 0% before it
 * starts and 100% once it's over.
 */
export function budgetProgress(budget: BreadBudget, today: Dayjs): string {
  const start = budget.beginDateTimestampSecs.valueOf();
  const end = budget.endDateTimestampSecs.valueOf();
  const passed = end > start ? (today.valueOf() - start) / (end - start) : 1;
  return `${Math.round(Math.min(Math.max(passed, 0), 1) * 100)}% complete`;
}
